/**
 * The firm's own record, and how it looks to clients.
 *
 * TWO SEPARATE RESOURCES, ON PURPOSE, with two separate capabilities:
 *
 *   `organization/` is the firm's record — its name, country, timezone, currency and
 *   the word it uses for a matter. PATCHed, and `slug`, `region`, `status` and the
 *   single-practitioner acknowledgement are read-only: a firm renaming itself is
 *   ordinary, a firm lifting its own suspension or relaxing a two-person control is not.
 *
 *   `organization/branding/` is CLIENT-FACING content — it is rendered into the portal
 *   and into email. It needs `branding.view` / `branding.edit`, which somebody can hold
 *   without holding the firm's record, so it loads and fails independently rather than
 *   taking the page down with it.
 *
 * Branding is written with PUT, as a whole set. A partial update that left a stale logo
 * beside a new colour scheme would be a worse outcome on a client's screen than
 * resending everything.
 *
 * `matter_label` is the field to be careful with: it is the firm's own word for a
 * matter, and it is rendered throughout the staff application in place of "Case".
 * Changing it changes every screen in the product.
 */

import {
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type FirmBranding,
  type FirmPaymentDetails,
  type OrganizationProfile,
} from '@legalcrm/api';
import {
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Form,
  FormActions,
  FormInput,
  FormTextarea,
  Skeleton,
  useToast,
  useZodForm,
} from '@legalcrm/ui';
import { z } from 'zod';

import { useCan } from '@/auth/permissions';

const profileSchema = z.object({
  name: z.string().min(1, 'The firm needs a name'),
  country: z.string().max(2, 'A two-letter country code'),
  timezone: z.string().min(1, 'Enter a timezone'),
  base_currency: z.string().max(3, 'A three-letter currency code'),
  locale: z.string(),
  matter_label: z.string().min(1, 'Enter the word this firm uses for a matter'),
});

type ProfileValues = z.infer<typeof profileSchema>;

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const brandingSchema = z.object({
  logo_url: z.string().url('Enter a full URL').or(z.literal('')),
  primary_color: z.string().regex(HEX, 'A hex colour, e.g. #1f4ed8').or(z.literal('')),
  accent_color: z.string().regex(HEX, 'A hex colour, e.g. #1f4ed8').or(z.literal('')),
  portal_welcome: z.string().max(2000),
  support_email: z.string().email('Enter a valid email address').or(z.literal('')),
  support_phone: z.string().max(32),
});

type BrandingValues = z.infer<typeof brandingSchema>;

const paymentDetailsSchema = z.object({
  bank_name: z.string().max(200),
  account_title: z.string().max(200),
  account_number: z.string().max(64),
  iban: z.string().max(64),
  branch_code: z.string().max(32),
  instructions: z.string().max(2000),
});

type PaymentDetailsValues = z.infer<typeof paymentDetailsSchema>;

export function OrganizationPage() {
  return (
    <div className="flex flex-col gap-4">
      <FirmProfileCard />
      <PaymentDetailsCard />
      <BrandingCard />
    </div>
  );
}

function FirmProfileCard() {
  const api = useApi();
  const { toast } = useToast();

  const query = useApiQuery<OrganizationProfile>(qk.all('organization'), STAFF.organization);

  const form = useZodForm(profileSchema, {
    values: {
      name: query.data?.name ?? '',
      country: query.data?.country ?? '',
      timezone: query.data?.timezone ?? '',
      base_currency: query.data?.base_currency ?? '',
      locale: query.data?.locale ?? '',
      matter_label: query.data?.matter_label ?? '',
    },
  });

  const save = useApiMutation<OrganizationProfile, ProfileValues>(
    {
      mutationFn: (values) => api.patch<OrganizationProfile>(`${STAFF.organization}/`, values),
      // `me` carries `matter_label`, and the whole application renders it. Without this
      // the firm renames a matter and every other screen keeps the old word until a
      // reload.
      invalidates: [qk.all('organization'), qk.me()],
    },
    {
      onSuccess: () => toast({ title: 'Firm profile saved', tone: 'success' }),
    },
  );

  if (query.isLoading) return <Skeleton className="h-72 w-full" />;
  if (query.error || !query.data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const organization = query.data;
  const canEdit = organization._actions?.edit === true;

  return (
    <Card>
      <CardHeader
        title="Firm profile"
        description="The firm's own record. Its identifier, its region and its status are set by the platform and are not editable here."
      />
      <CardBody>
        <Form
          form={form}
          ariaLabel="Firm profile"
          error={save.error}
          onSubmit={(values) => save.mutateAsync(values)}
        >
          <FormInput form={form} name="name" label="Firm name" disabled={!canEdit} />

          <div className="grid gap-4 sm:grid-cols-2">
            <FormInput
              form={form}
              name="country"
              label="Country"
              hint="Two-letter code, e.g. GB."
              disabled={!canEdit}
            />
            <FormInput
              form={form}
              name="timezone"
              label="Timezone"
              hint="An IANA name, e.g. Europe/London."
              disabled={!canEdit}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormInput
              form={form}
              name="base_currency"
              label="Base currency"
              hint="Three-letter ISO code, e.g. PKR or USD."
              disabled={!canEdit}
            />
            <FormInput form={form} name="locale" label="Locale" disabled={!canEdit} />
          </div>

          <FormInput
            form={form}
            name="matter_label"
            label="What this firm calls a matter"
            hint="Rendered throughout the application in place of “Case”. Singular — the plural is derived."
            disabled={!canEdit}
          />

          <dl className="grid gap-3 rounded border border-line bg-surface-sunken p-3 sm:grid-cols-3">
            <ReadOnly label="Identifier">{organization.slug}</ReadOnly>
            <ReadOnly label="Region">{organization.region}</ReadOnly>
            <ReadOnly label="Status">{organization.status}</ReadOnly>
          </dl>

          {canEdit && <FormActions submitLabel="Save firm profile" busy={save.isPending} />}
        </Form>
      </CardBody>
    </Card>
  );
}

function BrandingCard() {
  const api = useApi();
  const { toast } = useToast();
  const canView = useCan('branding', 'view');
  const canEdit = useCan('branding', 'edit');

  const query = useApiQuery<FirmBranding>(qk.all('branding'), STAFF.organizationBranding, {
    enabled: canView,
  });

  const form = useZodForm(brandingSchema, {
    values: {
      logo_url: query.data?.logo_url ?? '',
      primary_color: query.data?.primary_color ?? '',
      accent_color: query.data?.accent_color ?? '',
      portal_welcome: query.data?.portal_welcome ?? '',
      support_email: query.data?.support_email ?? '',
      support_phone: query.data?.support_phone ?? '',
    },
  });

  const save = useApiMutation<FirmBranding, BrandingValues>(
    {
      // PUT, not PATCH — the whole set is resent so the portal never renders half of
      // one scheme beside half of another.
      mutationFn: (values) => api.put<FirmBranding>(`${STAFF.organizationBranding}/`, values),
      invalidates: [qk.all('branding')],
    },
    {
      onSuccess: () => toast({ title: 'Branding saved', tone: 'success' }),
    },
  );

  if (!canView) return null;
  if (query.isLoading) return <Skeleton className="h-72 w-full" />;
  if (query.error) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  return (
    <Card sensitivity="client-facing">
      <CardHeader
        title="Branding"
        description="Client-facing. Everything on this card is rendered into the client portal and into the email the firm sends."
      />
      <CardBody>
        <Form
          form={form}
          ariaLabel="Branding"
          error={save.error}
          onSubmit={(values) => save.mutateAsync(values)}
        >
          <FormInput
            form={form}
            name="logo_url"
            label="Logo URL"
            hint="A full https:// address. Shown at the top of the portal."
            disabled={!canEdit}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <FormInput
              form={form}
              name="primary_color"
              label="Primary colour"
              hint="Hex, e.g. #1f4ed8."
              disabled={!canEdit}
            />
            <FormInput
              form={form}
              name="accent_color"
              label="Accent colour"
              hint="Hex, e.g. #0ea5e9."
              disabled={!canEdit}
            />
          </div>

          <FormTextarea
            form={form}
            name="portal_welcome"
            label="Portal welcome"
            rows={4}
            hint="The first thing a client reads when they sign in. Written for them, not for the firm."
            disabled={!canEdit}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <FormInput
              form={form}
              name="support_email"
              label="Support email"
              type="email"
              disabled={!canEdit}
            />
            <FormInput
              form={form}
              name="support_phone"
              label="Support telephone"
              type="tel"
              disabled={!canEdit}
            />
          </div>

          {canEdit && (
            <FormActions
              submitLabel="Save branding"
              busy={save.isPending}
              aside="Saved as a set — every field above is written together."
            />
          )}
        </Form>
      </CardBody>
    </Card>
  );
}

/**
 * The account clients pay into, shown with every invoice -- in the client app and in the
 * email about a bill. Always the CURRENT details: nothing is copied onto an invoice, so a
 * change here reaches every unpaid bill at once.
 *
 * `organization.edit`, not a finance capability. Whoever can change this can redirect
 * every client payment, which is the firm's decision rather than billing staff's. Finance
 * reads the same details, read-only, on each invoice.
 */
function PaymentDetailsCard() {
  const api = useApi();
  const { toast } = useToast();
  const canView = useCan('organization', 'view');
  const canEdit = useCan('organization', 'edit');

  const query = useApiQuery<FirmPaymentDetails>(
    qk.all('payment-details'),
    STAFF.organizationPaymentDetails,
    { enabled: canView },
  );

  const form = useZodForm(paymentDetailsSchema, {
    values: {
      bank_name: query.data?.bank_name ?? '',
      account_title: query.data?.account_title ?? '',
      account_number: query.data?.account_number ?? '',
      iban: query.data?.iban ?? '',
      branch_code: query.data?.branch_code ?? '',
      instructions: query.data?.instructions ?? '',
    },
  });

  const save = useApiMutation<FirmPaymentDetails, PaymentDetailsValues>(
    {
      mutationFn: (values) =>
        api.put<FirmPaymentDetails>(`${STAFF.organizationPaymentDetails}/`, values),
      // Invoices carry the details the client sees, so an open invoice must not keep
      // showing the old account.
      invalidates: [qk.all('payment-details'), qk.all('invoices')],
    },
    {
      onSuccess: () => toast({ title: 'Bank details saved', tone: 'success' }),
    },
  );

  if (!canView) return null;
  if (query.isLoading) return <Skeleton className="h-72 w-full" />;
  if (query.error) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  return (
    <Card sensitivity="client-facing">
      <CardHeader
        title="Bank details for invoices"
        description="Shown to clients on every invoice."
      />
      <CardBody>
        <Form
          form={form}
          ariaLabel="Bank details for invoices"
          error={save.error}
          onSubmit={(values) => save.mutateAsync(values)}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormInput form={form} name="bank_name" label="Bank name" disabled={!canEdit} />
            <FormInput
              form={form}
              name="account_title"
              label="Account title"
              hint="The name the account is held in."
              disabled={!canEdit}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormInput
              form={form}
              name="account_number"
              label="Account number"
              disabled={!canEdit}
            />
            <FormInput form={form} name="iban" label="IBAN" disabled={!canEdit} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormInput form={form} name="branch_code" label="Branch code" disabled={!canEdit} />
          </div>

          <FormTextarea
            form={form}
            name="instructions"
            label="Payment instructions"
            rows={4}
            hint="Anything else a client needs to pay correctly, e.g. which reference to use."
            disabled={!canEdit}
          />

          {canEdit && (
            <FormActions
              submitLabel="Save bank details"
              busy={save.isPending}
              aside="Leave every field empty to show clients no bank details."
            />
          )}
        </Form>
      </CardBody>
    </Card>
  );
}

function ReadOnly({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className="mt-0.5 truncate text-sm text-ink">{children}</dd>
    </div>
  );
}
